import { useMemo } from 'react';
import { Breadcrumbs, BreadcrumbItem } from '@nextui-org/react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaListUl, FaThLarge } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import { getTopicBySlug, themes } from '../data';
import {
    persistThematicStudiesViewMode,
    resolveThematicStudiesViewMode,
    THEMATIC_STUDIES_VIEW_PARAM,
    type ThematicStudiesViewMode,
} from '../viewMode';

interface Crumb {
    title: string;
    path: string;
    isCurrent?: boolean;
}

const boxAnimation = {
    initial: {
        y: '50%',
        opacity: 0,
        scale: 0.5,
    },
    animate: {
        y: 0,
        opacity: 1,
        scale: 1,
    },
    exit: {
        y: '50%',
        opacity: 0,
        transition: {
            duration: 0.25,
        },
    },
    transition: {
        duration: 0.25,
        ease: 'easeOut',
    },
};

export default function StudyBreadcrumbs() {
    const location = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const viewMode = resolveThematicStudiesViewMode(searchParams.get(THEMATIC_STUDIES_VIEW_PARAM));

    const breadcrumbItems = useMemo(() => {
        const items: Crumb[] = [{ title: 'Home', path: '/' }];

        const pathSegments = location.pathname.split('/').filter(Boolean);
        if (pathSegments[0] !== 'thematic-studies') {
            return items;
        }

        items.push({ title: 'Thematic Studies', path: '/thematic-studies' });

        if (pathSegments[1]) {
            const theme = themes.find((candidate) => candidate.slug === pathSegments[1]);
            if (theme) {
                items.push({
                    title: theme.name,
                    path: `/thematic-studies/${pathSegments[1]}`,
                });
            }
        }

        if (pathSegments[2]) {
            const topic = getTopicBySlug(pathSegments[2]);
            if (topic) {
                items.push({
                    title: topic.title,
                    path: `/thematic-studies/${pathSegments[1]}/${pathSegments[2]}`,
                    isCurrent: true,
                });
            }
        }

        return items;
    }, [location.pathname]);

    if (breadcrumbItems.length <= 1) {
        return null;
    }

    const pathSegments = location.pathname.split('/').filter(Boolean);
    const isThematicStudiesIndexPage = pathSegments.length === 1 && pathSegments[0] === 'thematic-studies';

    const buildTargetPath = (nextView: ThematicStudiesViewMode): string => {
        const params = new URLSearchParams(searchParams.toString());
        params.set(THEMATIC_STUDIES_VIEW_PARAM, nextView);
        const nextSearch = params.toString();
        return nextSearch ? `${location.pathname}?${nextSearch}` : location.pathname;
    };

    const handleViewModeClick = (nextView: ThematicStudiesViewMode): void => {
        persistThematicStudiesViewMode(nextView);
    };

    return (
        <div className="m-8 md:mx-28 md:my-10">
            <AnimatePresence>
                <motion.div key="breadcrumb-box" {...boxAnimation}>
                    <div className="mb-8 ml-0.5 flex flex-wrap items-start justify-between gap-4 sm:ml-1 sm:items-center">
                        <div className="scrollbar-hide min-w-0 overflow-x-auto">
                            <Breadcrumbs
                                color="primary"
                                size="lg"
                                separator="/"
                                itemClasses={{
                                    item: 'text-xl text-darkBlue dark:text-lightGrey data-[current=true]:text-lightOrange dark:data-[current=true]:text-lightBlue',
                                    separator: 'px-2',
                                }}
                                className={`${breadcrumbItems.length > 1 ? 'opacity-100' : 'opacity-0'} transition duration-250 ease-in-out`}
                            >
                                {breadcrumbItems.map((item, index) => {
                                    const isLast = index === breadcrumbItems.length - 1;
                                    const isCurrent = item.isCurrent || isLast;

                                    return (
                                        <BreadcrumbItem key={item.path} isCurrent={isCurrent} className="text-xl font-bold">
                                            {isCurrent ? <span>{item.title}</span> : <Link to={item.path}>{item.title}</Link>}
                                        </BreadcrumbItem>
                                    );
                                })}
                            </Breadcrumbs>
                        </div>
                        {isThematicStudiesIndexPage && (
                            <div
                                className="inline-flex items-center gap-1 rounded-lg border border-default-200 bg-white/80 p-1 shadow-sm dark:border-default-700 dark:bg-default-900/70"
                                role="group"
                                aria-label="Thematic Studies view mode"
                            >
                                <Link
                                    to={buildTargetPath('themes')}
                                    replace
                                    onClick={() => handleViewModeClick('themes')}
                                    aria-label="Themes view"
                                    aria-pressed={viewMode === 'themes'}
                                    title="Themes view"
                                    className={`rounded-md p-2 transition-colors ${
                                        viewMode === 'themes'
                                            ? 'bg-darkBlue text-white dark:bg-lightBlue dark:text-black'
                                            : 'text-default-500 hover:bg-default-100 hover:text-darkBlue dark:text-default-400 dark:hover:bg-default-800 dark:hover:text-lightBlue'
                                    }`}
                                >
                                    <FaThLarge className="text-base" aria-hidden="true" />
                                </Link>
                                <Link
                                    to={buildTargetPath('all')}
                                    replace
                                    onClick={() => handleViewModeClick('all')}
                                    aria-label="All topics view"
                                    aria-pressed={viewMode === 'all'}
                                    title="All topics view"
                                    className={`rounded-md p-2 transition-colors ${
                                        viewMode === 'all'
                                            ? 'bg-darkBlue text-white dark:bg-lightBlue dark:text-black'
                                            : 'text-default-500 hover:bg-default-100 hover:text-darkBlue dark:text-default-400 dark:hover:bg-default-800 dark:hover:text-lightBlue'
                                    }`}
                                >
                                    <FaListUl className="text-base" aria-hidden="true" />
                                </Link>
                            </div>
                        )}
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
}
